import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useUserData } from "../../auth/useUserData.js";
import { usePdsCrmSourceAccess_ConnectionQuery } from "./PdsCrmSourceAccess.generated.js";

export interface PdsCrmSourceAccess {
  /** READ_CRM + INTEGRATIONS + pds_sync connected. */
  isEnabled: boolean;
  loading: boolean;
}

/**
 * Gates the "only PDS-synced contacts" filter and the "Last PDS sync" column.
 *
 * READ_CRM rather than the review surfaces' MANAGE_CRM (MEIS-12729 D2): those
 * are gated to avoid a dead-end modal and to keep customer PII off
 * non-actioners' screens, and neither applies to a filter that only narrows
 * rows the user can already see.
 */
export function usePdsCrmSourceAccess(): PdsCrmSourceAccess {
  const { hasOrganisationPermission, hasCapability } = useUserData();
  const client = useApolloClient();

  const hasGate =
    hasOrganisationPermission("READ_CRM") && hasCapability("INTEGRATIONS");

  const connectionQuery = usePdsCrmSourceAccess_ConnectionQuery({
    client,
    skip: !hasGate,
    // Revalidate on mount: connecting or disconnecting pds_sync mid-session must
    // not leave the filter and column wrongly shown or hidden. Matches
    // usePdsSyncReviewAccess. What this hook deliberately does NOT copy is that
    // one's 30s task-count polling, which a read-only gate has no use for.
    fetchPolicy: "cache-and-network",
  });

  const isConnected = Boolean(
    getDataOrNull(connectionQuery.data?.organisationIntegration)
      ?.organisationIntegration
  );

  return {
    isEnabled: hasGate && isConnected,
    loading: connectionQuery.loading,
  };
}
